import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import AdminLayout from "./AdminLayout";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, MessageCircle, ChevronDown, ChevronUp, Monitor, Globe, User } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { UserFeedback } from "@shared/schema";

const TYPE_LABELS: Record<string, string> = {
  bug: "Bug Report",
  problem: "Problem",
  suggestion: "Suggestion",
  other: "Other",
};

const TYPE_VARIANTS: Record<string, "destructive" | "secondary" | "default" | "outline"> = {
  bug: "destructive",
  problem: "outline",
  suggestion: "default",
  other: "secondary",
};

const STATUS_LABELS: Record<string, string> = {
  new: "New",
  reviewed: "Reviewed",
  resolved: "Resolved",
};

function formatDate(date: string | Date | null | undefined) {
  if (!date) return "—";
  return new Date(date).toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

function FeedbackRow({ item }: { item: UserFeedback }) {
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState(item.adminNotes || "");
  const [editingNotes, setEditingNotes] = useState(false);
  const { toast } = useToast();

  const updateMutation = useMutation({
    mutationFn: (data: { status?: string; adminNotes?: string }) =>
      apiRequest("PATCH", `/api/admin/feedback/${item.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/feedback"] });
      toast({ title: "Updated" });
      setEditingNotes(false);
    },
    onError: () => {
      toast({ title: "Failed to update", variant: "destructive" });
    },
  });

  return (
    <Card className="w-full">
      <CardHeader className="p-4 pb-3">
        <div className="flex flex-wrap items-start gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1.5">
              <Badge variant={TYPE_VARIANTS[item.type] || "secondary"}>
                {TYPE_LABELS[item.type] || item.type}
              </Badge>
              <Badge variant={item.status === "resolved" ? "secondary" : item.status === "reviewed" ? "outline" : "default"}>
                {STATUS_LABELS[item.status] || item.status}
              </Badge>
              <span className="text-xs text-muted-foreground">{formatDate(item.createdAt)}</span>
            </div>
            <p className="text-sm text-foreground line-clamp-2">{item.message}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Select
              value={item.status}
              onValueChange={(val) => updateMutation.mutate({ status: val })}
              disabled={updateMutation.isPending}
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="reviewed">Reviewed</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
              </SelectContent>
            </Select>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setExpanded(!expanded)}
              aria-label={expanded ? "Collapse" : "Expand"}
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="px-4 pb-4 pt-0 space-y-3 border-t">
          <div className="pt-3">
            <p className="text-sm font-medium text-muted-foreground mb-1">Full Message</p>
            <p className="text-sm whitespace-pre-wrap bg-muted/40 rounded-md p-3">{item.message}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {(item.userName || item.userEmail) && (
              <div className="flex items-start gap-2">
                <User className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-muted-foreground text-xs">Submitted by</p>
                  <p className="truncate">{item.userName || "—"}</p>
                  {item.userEmail && <p className="text-muted-foreground text-xs truncate">{item.userEmail}</p>}
                </div>
              </div>
            )}
            {item.pageUrl && (
              <div className="flex items-start gap-2">
                <Globe className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-muted-foreground text-xs">Page URL</p>
                  <p className="break-all text-xs">{item.pageUrl}</p>
                </div>
              </div>
            )}
            {item.screenSize && (
              <div className="flex items-start gap-2">
                <Monitor className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-muted-foreground text-xs">Screen Size</p>
                  <p>{item.screenSize}</p>
                </div>
              </div>
            )}
            {item.userId && (
              <div>
                <p className="text-muted-foreground text-xs">User ID</p>
                <p className="font-mono text-xs break-all">{item.userId}</p>
              </div>
            )}
          </div>

          {item.userAgent && (
            <div>
              <p className="text-muted-foreground text-xs mb-1">User Agent</p>
              <p className="text-xs text-muted-foreground break-all font-mono bg-muted/30 rounded p-2">{item.userAgent}</p>
            </div>
          )}

          <div>
            <p className="text-sm font-medium text-muted-foreground mb-1.5">Admin Notes</p>
            {editingNotes ? (
              <div className="space-y-2">
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add internal notes…"
                  rows={3}
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => updateMutation.mutate({ adminNotes: notes })}
                    disabled={updateMutation.isPending}
                  >
                    {updateMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                    Save Notes
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setEditingNotes(false); setNotes(item.adminNotes || ""); }}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div
                className="text-sm text-muted-foreground bg-muted/30 rounded-md p-2 min-h-[40px] cursor-pointer hover-elevate"
                onClick={() => setEditingNotes(true)}
              >
                {item.adminNotes || <span className="italic">Click to add notes…</span>}
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

export default function AdminFeedback() {
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const { toast } = useToast();

  const { data, isLoading, isError } = useQuery<{ items: UserFeedback[]; total: number }>({
    queryKey: ["/api/admin/feedback", statusFilter, typeFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (typeFilter !== "all") params.set("type", typeFilter);
      const res = await fetch(`/api/admin/feedback?${params}`);
      if (!res.ok) throw new Error("Failed to fetch feedback");
      return res.json();
    },
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <AdminLayout>
      <div className="p-6 max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold">User Feedback</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Bug reports, suggestions, and problems submitted by users.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="reviewed">Reviewed</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
            </SelectContent>
          </Select>

          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="bug">Bug Reports</SelectItem>
              <SelectItem value="problem">Problems</SelectItem>
              <SelectItem value="suggestion">Suggestions</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>

          {total > 0 && (
            <span className="text-sm text-muted-foreground ml-auto">
              {total} {total === 1 ? "item" : "items"}
            </span>
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : isError ? (
          <div className="text-center py-16 text-muted-foreground">
            <p className="font-medium">Failed to load feedback</p>
            <p className="text-sm mt-1">Please try refreshing the page.</p>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <MessageCircle className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="font-medium">No feedback yet</p>
            <p className="text-sm mt-1">
              {statusFilter !== "all" || typeFilter !== "all"
                ? "Try adjusting your filters."
                : "Feedback submitted by users will appear here."}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item) => (
              <FeedbackRow key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
